import AppLayoutSidebar from './AppLayoutSidebar';
import Avatar from 'Components/Atoms/Avatar/Avatar';
import Icon from 'Components/Atoms/Icon/Icon';
import PropTypes from 'prop-types';
import Drawer from 'Components/Molecules/Drawer/Drawer';
import { useEffect, useState } from 'react';
import { ROUTES } from 'Utils/constants';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import { motion, AnimatePresence } from 'framer-motion';
import OutlineChevronRight from 'Components/Atoms/OutlineIcons/OutlineChevronRight';
import OutlineChevronLeft from 'Components/Atoms/OutlineIcons/OutlineChevronLeft';
import ChangeWorkspaceButton from 'Components/Organisms/ChangeWorkspaceButton/ChangeWorkspaceButton';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import useProjectSelected from 'Hooks/useProjectSelected';

export default function LayoutSidebarMobile(props) {
    const { open, onClose } = props;
    const [openWorkspaceMenu, setOpenWorkspaceMenu] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const { user, logout } = useUser();
    const { workspace, isLoading } = useWorkspaceSelected();
    const { project } = useProjectSelected();
    const workspaceName = workspace?.name || '';
    const projectName = project?.name || '';

    useEffect(() => {
        if (open) {
            window.scrollTo(0, 0);
            return () => setOpenWorkspaceMenu(false);
        }
    }, [open]);

    const menuItems = [
        {
            icon: 'user',
            children: t('edit-profile'),
            onClick: () => {
                navigate(ROUTES.profile);
                onClose();
            },
        },
        ...(user?.is_partner || user?.is_support
            ? [
                  {
                      icon: 'coins',
                      children: t('partners'),
                      onClick: () => {
                          navigate(ROUTES.partners);
                          onClose();
                      },
                  },
              ]
            : []),
        {
            icon: 'exit',
            children: t('logout'),
            onClick: () => logout(),
        },
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="xs"
            classes={{
                wrapper: 'h-[calc(100dvh-61px)] top-[61px] z-mobile-sidebar overflow-clip',
            }}
            className="px-3 py-4 overflow-auto shadow-[-10px_0_84px_rgba(0,0,0,0.15)] overflow-x-hidden"
        >
            <AnimatePresence initial={false} mode="wait">
                {!openWorkspaceMenu && (
                    <motion.div
                        key="main-menu"
                        className="flex flex-col h-full gap-2"
                        initial={{ x: -100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -100, opacity: 0 }}
                        transition={{ ease: 'easeInOut', duration: 0.15 }}
                    >
                        <AppLayoutSidebar />

                        <div className="grow" />
                        <button
                            type="button"
                            className="group flex items-center gap-3 text-left text-dark-blue px-2 py-3"
                            onClick={() => setOpenWorkspaceMenu(true)}
                        >
                            <Avatar name={projectName} className="shrink-0" />

                            <div className="text-xs grow max-w-[130px] overflow-hidden">
                                <div className="font-medium truncate">{projectName}</div>
                                {isLoading ? (
                                    <Skeleton variant="text" width={90} />
                                ) : (
                                    <div className="text-gray-500 truncate">{workspaceName}</div>
                                )}
                            </div>

                            <OutlineChevronRight size={20} color="currentColor" className="shrink-0 text-dark-blue" />
                        </button>
                    </motion.div>
                )}
                {openWorkspaceMenu && (
                    <motion.div
                        key="workspace-menu"
                        className="flex flex-col h-full gap-2"
                        initial={{ x: 100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 100, opacity: 0 }}
                        transition={{ ease: 'easeInOut', duration: 0.15 }}
                    >
                        <button
                            type="button"
                            className="flex items-center gap-3 text-left text-dark-blue px-2 py-3"
                            onClick={() => setOpenWorkspaceMenu(false)}
                        >
                            <OutlineChevronLeft size={20} color="currentColor" className="shrink-0 text-dark-blue" />
                            <Avatar name={projectName} className="shrink-0" />

                            <div className="text-xs grow max-w-[140px] overflow-hidden">
                                <div className="font-medium truncate">{projectName}</div>
                                {isLoading ? (
                                    <Skeleton variant="text" width={90} />
                                ) : (
                                    <div className="text-gray-500 truncate">{workspaceName}</div>
                                )}
                            </div>
                        </button>

                        <div className="py-1">
                            <div className="h-[1px] bg-gray-200" />
                            <ChangeWorkspaceButton className="py-5 px-3 w-full" />
                            <div className="h-[1px] bg-gray-200" />
                        </div>

                        <ul className="flex flex-col gap-1.5">
                            {menuItems.map((item, index) => (
                                <li key={index}>
                                    <button
                                        type="button"
                                        className="group flex w-full text-left py-2 px-3 rounded-md hover:bg-gray-100 transition-colors gap-3 text-gray-500 hover:text-dark-blue text-sm items-center"
                                        onClick={item.onClick}
                                    >
                                        <Icon
                                            name={item.icon}
                                            size={20}
                                            color="currentColor"
                                            className="shrink-0 text-dark-blue/50 group-hover:text-dark-blue transition-colors"
                                        />
                                        <div className="text-sm grow">{item.children}</div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </Drawer>
    );
}

LayoutSidebarMobile.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
